// Model registry — mirrors Photon's catalog (cpopsa/models/photon-models.json)
// and slider tables (cpopsa/src/state/processing.hpp). Ids are Photon's package
// ids; stem names, titles and colours are the app's, so the widget and the app
// agree on what each stem looks like.

export type Hex = `#${string}`;

// cpopsa/src/state/processing.hpp kColor* constants.
export const PHOTON = {
  blue: '#4A90D9',
  orange: '#E89850',
  salmon: '#F28C73',
  green: '#66BB6A',
  purple: '#AB7AC4',
  teal: '#4DB6AC',
  gray: '#808080',
  veSpeech: '#6D9C70',   // Strength dot / Speaker 1
} as const satisfies Record<string, Hex>;

export type MenuSection = 'Denoise' | 'Demix';

export interface StemDef {
  name: string;      // lowercase id, also the mp3 / peaks key
  title: string;     // row label
  color: Hex;
  filename: string;
}

interface ModelBase {
  id: string;
  label: string;
  version: string;    // shown after the name in the menu, like the app
  clipName: string;   // panel header (file-names-for-web-widget.md, Oct 27)
  enabled?: boolean;  // default true; false keeps the model out of the menu
}

// Demix models: N stems mixed with dB sliders, S/M, overlaid waveform.
export interface DemixModel extends ModelBase {
  kind: 'demix';
  section: 'Demix';
  stems: StemDef[];   // STATE order = waveform Z-order (index 0 is backmost)
  speakers?: number;  // Voice Separator: renders the "Speakers" row above the sliders
}

// Denoise models: one Strength slider crossfading a before/after pair, two-layer
// waveform (Photon's VoiceEnhance display).
export interface RestoreModel extends ModelBase {
  kind: 'restore';
  section: 'Denoise';
  before: string;
  after: string;
  defaultStrength: number;   // 0..1
}

export type ModelDef = DemixModel | RestoreModel;

export const STRENGTH_COLOR: Hex = PHOTON.veSpeech;
export const RESTORE_SOURCES = ['before', 'after'] as const;

const stem = (name: string, title: string, color: Hex): StemDef => ({
  name, title, color, filename: `${name}.mp3`,
});

const restore = (
  id: string, label: string, version: string, clipName: string, enabled = true,
): RestoreModel => ({
  kind: 'restore', section: 'Denoise', id, label, version, clipName, enabled,
  before: 'before.mp3', after: 'after.mp3', defaultStrength: 0.8,
});

// Menu order: Photon's dropdown lists its "Denoise" section first, then "Demix"
// (cpopsa/src/gui/dropdown.hpp rebuild()).
export const MODELS: readonly ModelDef[] = [
  restore('voice-restore', 'Voice Restore', 'v1.2', 'voiceexample.wav'),
  restore('indoor', 'Indoor', 'v1.1', 'indoorexample.wav'),
  restore('outdoor-wind', 'Outdoor Wind', 'v1.1', 'outdoorexample.wav'),
  {
    kind: 'demix', section: 'Demix',
    id: 'cinema-4-stems', label: 'Cinema - 4 Stems', version: 'v1.1', clipName: 'cinemaexample.wav',
    stems: [
      stem('music', 'Music', PHOTON.blue),
      stem('effects', 'Effects', PHOTON.orange),
      stem('other', 'Other', PHOTON.purple),
      stem('voice', 'Voice', PHOTON.green),
    ],
  },
  // Disabled (Gabe, Oct 27): the Ultra model is the better demo; one Music
  // entry is enough.
  {
    kind: 'demix', section: 'Demix', enabled: false,
    id: 'music-4-stems', label: 'Music - 4 Stems', version: 'v1.1', clipName: 'example4stems.wav',
    stems: [
      stem('drums', 'Drums', PHOTON.blue),
      stem('bass', 'Bass', PHOTON.orange),
      stem('other', 'Other', PHOTON.purple),
      stem('voice', 'Voice', PHOTON.green),
    ],
  },
  {
    kind: 'demix', section: 'Demix',
    id: 'music4hq', label: 'Music - 4 Stems Ultra', version: 'v1.1', clipName: 'example4stems.wav',
    stems: [
      stem('drums', 'Drums', PHOTON.blue),
      stem('bass', 'Bass', PHOTON.orange),
      stem('other', 'Other', PHOTON.purple),
      stem('vocals', 'Vocals', PHOTON.green),
    ],
  },
  {
    kind: 'demix', section: 'Demix',
    id: 'drumsep', label: 'Drums - 5 Stems', version: 'v1.3', clipName: 'drumexample.wav',
    stems: [
      stem('kick', 'Kick', PHOTON.blue),
      stem('snare', 'Snare', PHOTON.salmon),
      stem('toms', 'Toms', PHOTON.orange),
      stem('hihat', 'Hi-Hat', PHOTON.green),
      stem('cymbals', 'Cymbals', PHOTON.purple),
    ],
  },
  {
    kind: 'demix', section: 'Demix',
    id: 'voice-2-stems', label: 'Voice - 2 Stems', version: 'v1.1', clipName: '2stems.wav',
    stems: [
      stem('other', 'Other', PHOTON.purple),
      stem('voice', 'Voice', PHOTON.green),
    ],
  },
  {
    kind: 'demix', section: 'Demix',
    id: 'voice-separator', label: 'Voice Separator', version: 'v1.2', clipName: 'mutiplespeakers.wav',
    speakers: 2,
    stems: [
      stem('other', 'Other', PHOTON.purple),
      stem('speaker1', 'Speaker 1', PHOTON.veSpeech),
      stem('speaker2', 'Speaker 2', PHOTON.orange),
    ],
  },
];

// Music - 4 Stems Ultra: the best-looking and -sounding demo (Gabe, Oct 27).
export const DEFAULT_MODEL_ID = 'music4hq';

export function getModel(id: string): ModelDef {
  const model = MODELS.find((m) => m.id === id);
  if (!model) {
    throw new Error(`Unknown model: ${id}`);
  }
  return model;
}

export const isEnabled = (m: ModelDef): boolean => m.enabled !== false;

export function menuSections(
  models: readonly ModelDef[] = MODELS,
): Array<{ title: MenuSection; models: ModelDef[] }> {
  const order: MenuSection[] = ['Denoise', 'Demix'];
  return order
    .map((title) => ({ title, models: models.filter((m) => isEnabled(m) && m.section === title) }))
    .filter((s) => s.models.length > 0);
}

// Slider display order (cpopsa ModelSettingsViews.cpp, designer 08-28 #R7):
// Voice/Vocals/Dialog first, then Bass, Drums, Other; everything else keeps its
// state order. A pure permutation of the VIEW — indices still point at the
// state stem, so the waveform's Z-order is untouched.
export function displayOrder(stems: readonly StemDef[]): number[] {
  const rank = (name: string): number => {
    const n = name.toLowerCase();
    if (n.includes('voice') || n.includes('vocal') || n.includes('dialog')) return 0;
    if (n.includes('bass')) return 1;
    if (n.includes('drum')) return 2;
    if (n.includes('other')) return 3;
    return 4;
  };
  return stems
    .map((s, i) => ({ i, r: rank(s.name) }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map((e) => e.i);
}

// Audio sources in engine order: the stems (state order) or [before, after].
export function sourceNames(model: ModelDef): string[] {
  return model.kind === 'demix' ? model.stems.map((s) => s.name) : [...RESTORE_SOURCES];
}

export function sourceFiles(model: ModelDef): string[] {
  return model.kind === 'demix' ? model.stems.map((s) => s.filename) : [model.before, model.after];
}
